import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0a0a0a] shadow-lg shadow-black/20' : 'bg-[#0a0a0a]'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#00f2ea] to-[#ff0050] rounded-lg flex items-center justify-center shadow-lg shadow-[#00f2ea]/30">
                <span className="text-white font-bold text-lg">T</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">TikScale</h1>
                <p className="text-xs text-gray-400 hidden md:block">Escale seu TikTokShop</p>
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-gray-300 hover:text-[#00f2ea] transition-colors cursor-pointer whitespace-nowrap">Como Funciona</a>
              <a href="#pricing" className="text-sm font-medium text-gray-300 hover:text-[#00f2ea] transition-colors cursor-pointer whitespace-nowrap">Preços</a>
              <div className="flex items-center gap-3">
                <a href="https://tikscale.site/auth" className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap border border-gray-600 hover:border-[#00f2ea] rounded-lg">Login</a>
                <button className="px-5 py-2.5 bg-gradient-to-r from-[#00f2ea] to-[#ff0050] text-white rounded-lg font-semibold text-sm hover:shadow-lg hover:shadow-[#00f2ea]/50 transition-all cursor-pointer whitespace-nowrap">Começar Agora</button>
              </div>
            </div>
            <button className="lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-menu-line text-2xl text-white"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0a] pb-12">
        <div className="absolute inset-0 bg-[#0a0a0a]"></div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-24 text-center">
          {/* Floating particles and decorative elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-[#00f2ea] rounded-full animate-ping opacity-40"></div>
            <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-[#ff0050] rounded-full animate-pulse opacity-30"></div>
            <div className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-[#00f2ea] rounded-full animate-ping opacity-20"></div>
            <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-[#ff0050] rounded-full animate-pulse opacity-25"></div>
            <div className="absolute top-2/3 left-1/2 w-1 h-1 bg-[#00f2ea] rounded-full animate-ping opacity-30"></div>
            <div className="absolute top-1/5 right-1/5 w-1.5 h-1.5 bg-[#ff0050] rounded-full animate-pulse opacity-20"></div>
            
            {/* Corner borders */}
            <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-[#00f2ea]/20 animate-pulse"></div>
            <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-[#ff0050]/20 animate-pulse"></div>
            <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-[#00f2ea]/20 animate-pulse"></div>
            <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-[#ff0050]/20 animate-pulse"></div>
            
            {/* Horizontal lines */}
            <div className="absolute top-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00f2ea]/20 to-transparent animate-pulse"></div>
            <div className="absolute top-2/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff0050]/20 to-transparent animate-pulse"></div>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-[#00f2ea]/10 backdrop-blur-sm rounded-full mb-8 border border-[#00f2ea]/30 animate-fade-in hover:bg-[#00f2ea]/20 transition-all duration-300">
            <div className="w-1.5 md:w-2 h-1.5 md:h-2 bg-[#00f2ea] rounded-full animate-pulse shadow-lg shadow-[#00f2ea]/50"></div>
            <span className="text-xs md:text-sm font-medium bg-gradient-to-r from-[#00f2ea] to-[#ff0050] bg-clip-text text-transparent">Plataforma de Crescimento no TikTokShop</span>
          </div>

          {/* Logo and Brand */}
          <div className="flex items-center justify-center gap-3 mb-6 animate-fade-in-up">
            <div className="w-12 h-12 bg-gradient-to-br from-[#00f2ea] to-[#ff0050] rounded-lg flex items-center justify-center relative overflow-hidden group shadow-lg shadow-[#00f2ea]/30">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <div className="absolute inset-0 border-2 border-[#00f2ea]/50 rounded-lg animate-pulse"></div>
              <span className="text-white font-bold text-2xl relative z-10">T</span>
            </div>
            <h2 className="text-4xl font-bold text-white relative">
              TikScale
              <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#00f2ea] to-[#ff0050] group-hover:w-full transition-all duration-500"></div>
            </h2>
          </div>

          {/* Main Title */}
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
            Escale seu TikTokShop<br />
            <span className="bg-gradient-to-r from-[#00f2ea] to-[#ff0050] bg-clip-text text-transparent relative">
              Automaticamente
              <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#00f2ea] to-[#ff0050] animate-pulse"></div>
              <div className="absolute -top-2 -right-2 w-2 h-2 bg-[#ff0050] rounded-full animate-ping opacity-60"></div>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-2xl text-gray-300 mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in-up relative">
            Aumente suas vendas e faturamento<span className="hidden md:inline"> no TikTokShop, em poucos cliques!</span>
            <div className="absolute -left-4 top-1/2 w-1 h-1 bg-[#00f2ea] rounded-full animate-pulse opacity-40"></div>
            <div className="absolute -right-4 top-1/2 w-1 h-1 bg-[#ff0050] rounded-full animate-pulse opacity-40"></div>
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-row items-center justify-center gap-4 mb-12 animate-fade-in-up">
            <button className="px-4 md:px-8 py-2 md:py-4 bg-gradient-to-r from-[#00f2ea] to-[#ff0050] text-white rounded-lg font-bold text-sm md:text-lg hover:shadow-xl hover:shadow-[#00f2ea]/50 transition-all cursor-pointer whitespace-nowrap relative overflow-hidden group hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <div className="absolute inset-0 border-2 border-[#00f2ea]/0 group-hover:border-[#00f2ea]/50 transition-all duration-300 rounded-lg"></div>
              <span className="relative z-10">Ver Planos</span>
            </button>
            <button className="px-4 md:px-8 py-2 md:py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold text-sm md:text-lg hover:bg-white/20 transition-all border border-white/20 cursor-pointer whitespace-nowrap relative overflow-hidden group hover:border-[#00f2ea]/50 hover:scale-105">
              <div className="absolute inset-0 border border-[#00f2ea]/0 group-hover:border-[#00f2ea]/30 transition-all duration-300 rounded-lg"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00f2ea]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <span className="relative z-10">Saiba Mais</span>
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="relative">
            <i className="ri-arrow-down-line text-3xl text-white/60"></i>
            <div className="absolute inset-0 blur-md bg-[#00f2ea]/20 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="relative py-8 bg-[#0a0a0a] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 animate-pulse"></div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00f2ea] to-transparent animate-pulse"></div>
          <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff0050] to-transparent animate-pulse"></div>
          <div className="absolute top-2/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00f2ea] to-transparent animate-pulse"></div>
        </div>
        
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-[#00f2ea] rounded-full animate-ping opacity-40"></div>
          <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-[#ff0050] rounded-full animate-pulse opacity-30"></div>
          <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-[#00f2ea] rounded-full animate-ping opacity-20"></div>
          <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-[#ff0050] rounded-full animate-pulse opacity-25"></div>
          <div className="absolute top-2/3 left-1/2 w-2 h-2 bg-[#00f2ea] rounded-full animate-ping opacity-30"></div>
          <div className="absolute top-1/5 right-1/5 w-3 h-3 bg-[#ff0050] rounded-full animate-pulse opacity-20"></div>
        </div>
        
        <div className="absolute top-0 right-1/3 w-[600px] h-[600px] bg-[#00f2ea]/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-0 left-1/3 w-[600px] h-[600px] bg-[#ff0050]/10 rounded-full blur-[120px] animate-pulse"></div>
        
        <div className="absolute top-12 left-12 w-24 h-24 border-l-2 border-t-2 border-[#00f2ea]/30 animate-pulse"></div>
        <div className="absolute top-12 right-12 w-24 h-24 border-r-2 border-t-2 border-[#ff0050]/30 animate-pulse"></div>
        <div className="absolute bottom-12 left-12 w-24 h-24 border-l-2 border-b-2 border-[#00f2ea]/30 animate-pulse"></div>
        <div className="absolute bottom-12 right-12 w-24 h-24 border-r-2 border-b-2 border-[#ff0050]/30 animate-pulse"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 relative">
              Veja como <span className="bg-gradient-to-r from-[#00f2ea] to-[#ff0050] bg-clip-text text-transparent relative">
                funciona
                <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#00f2ea] to-[#ff0050] animate-pulse"></div>
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto hidden md:block">Descubra como o TikScale pode transformar seu negócio no TikTok Shop</p>
          </div>
          
          <div className="relative bg-[#0a0a0a]/50 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-gray-800/50 hover:border-[#00f2ea]/30 transition-all">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00f2ea] via-[#ff0050] to-[#00f2ea] animate-pulse"></div>
            <div className="p-4 md:p-8">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe 
                  className="absolute inset-0 w-full h-full rounded-xl" 
                  src="https://www.youtube.com/embed/UMywHk4l6E0?si=iSTrqZL_cJqJvS5C" 
                  title="TikScale - Como Funciona" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <a 
              href="https://go.perfectpay.com.br/PPU38CQ6BQH" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="relative w-full px-6 py-4 rounded-lg font-bold text-lg transition-all cursor-pointer whitespace-nowrap overflow-hidden text-center bg-gradient-to-r from-[#00f2ea] to-[#ff0050] text-white hover:shadow-xl hover:shadow-[#ff0050]/50 hover:scale-105 block"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <div className="absolute inset-0 border border-[#00f2ea]/0 group-hover:border-[#00f2ea]/50 transition-all duration-300 rounded-lg"></div>
              <span className="relative z-10">Começar Agora</span>
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-12 bg-[#0a0a0a] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 animate-pulse"></div>
        </div>
        
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-[#00f2ea]/10 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#ff0050]/10 rounded-full blur-[100px] animate-pulse"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00f2ea]/10 backdrop-blur-sm rounded-full mb-4 border border-[#00f2ea]/30">
              <div className="w-1.5 h-1.5 bg-[#00f2ea] rounded-full animate-ping"></div>
              <span className="text-xs font-semibold bg-gradient-to-r from-[#00f2ea] to-[#ff0050] bg-clip-text text-transparent">Processo Simples</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Como <span className="bg-gradient-to-r from-[#00f2ea] to-[#ff0050] bg-clip-text text-transparent relative">
                funciona?
                <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-[#00f2ea] to-[#ff0050]"></div>
              </span>
            </h2>
            <p className="text-base text-gray-400 max-w-2xl mx-auto">Em apenas 4 passos você começa a crescer no TikTok de forma automática</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="group relative">
              <div className="hidden lg:block absolute top-14 left-[60%] w-full h-px">
                <div className="w-full h-full bg-gradient-to-r from-[#00f2ea]/30 to-transparent"></div>
              </div>
              <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-xl p-5 border border-gray-800 hover:border-[#00f2ea]/50 transition-all hover:shadow-xl hover:shadow-[#00f2ea]/10 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00f2ea]/0 to-[#00f2ea]/0 group-hover:from-[#00f2ea]/5 group-hover:to-transparent rounded-xl transition-all duration-500"></div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-3xl font-bold text-[#00f2ea]/20 group-hover:text-[#00f2ea]/40 transition-colors">01</div>
                    <div className="w-10 h-10 bg-gradient-to-br from-[#00f2ea]/20 to-[#ff0050]/20 rounded-lg flex items-center justify-center group-hover:from-[#00f2ea]/30 group-hover:to-[#ff0050]/30 transition-all group-hover:scale-110 border border-[#00f2ea]/30">
                      <i className="ri-user-add-line text-xl text-[#00f2ea]"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#00f2ea] transition-colors">Conecte sua Conta</h3>
                  <p className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">Conecte sua conta do TikTok de forma segura em poucos segundos</p>
                </div>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="group relative">
              <div className="hidden lg:block absolute top-14 left-[60%] w-full h-px">
                <div className="w-full h-full bg-gradient-to-r from-[#ff0050]/30 to-transparent"></div>
              </div>
              <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-xl p-5 border border-gray-800 hover:border-[#ff0050]/50 transition-all hover:shadow-xl hover:shadow-[#ff0050]/10 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-[#ff0050]/0 to-[#ff0050]/0 group-hover:from-[#ff0050]/5 group-hover:to-transparent rounded-xl transition-all duration-500"></div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-3xl font-bold text-[#ff0050]/20 group-hover:text-[#ff0050]/40 transition-colors">02</div>
                    <div className="w-10 h-10 bg-gradient-to-br from-[#ff0050]/20 to-[#00f2ea]/20 rounded-lg flex items-center justify-center group-hover:from-[#ff0050]/30 group-hover:to-[#00f2ea]/30 transition-all group-hover:scale-110 border border-[#ff0050]/30">
                      <i className="ri-settings-3-line text-xl text-[#ff0050]"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#ff0050] transition-colors">Configure Metas</h3>
                  <p className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">Defina suas metas de crescimento e público-alvo desejado</p>
                </div>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="group relative">
              <div className="hidden lg:block absolute top-14 left-[60%] w-full h-px">
                <div className="w-full h-full bg-gradient-to-r from-[#00f2ea]/30 to-transparent"></div>
              </div>
              <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-xl p-5 border border-gray-800 hover:border-[#00f2ea]/50 transition-all hover:shadow-xl hover:shadow-[#00f2ea]/10 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00f2ea]/0 to-[#00f2ea]/0 group-hover:from-[#00f2ea]/5 group-hover:to-transparent rounded-xl transition-all duration-500"></div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-3xl font-bold text-[#00f2ea]/20 group-hover:text-[#00f2ea]/40 transition-colors">03</div>
                    <div className="w-10 h-10 bg-gradient-to-br from-[#00f2ea]/20 to-[#ff0050]/20 rounded-lg flex items-center justify-center group-hover:from-[#00f2ea]/30 group-hover:to-[#ff0050]/30 transition-all group-hover:scale-110 border border-[#00f2ea]/30">
                      <i className="ri-rocket-line text-xl text-[#00f2ea]"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#00f2ea] transition-colors">Ative o Crescimento</h3>
                  <p className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">Ative a automação e deixe o TikScale trabalhar por você</p>
                </div>
              </div>
            </div>
            
            {/* Step 4 */}
            <div className="group relative">
              <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-xl p-5 border border-gray-800 hover:border-[#ff0050]/50 transition-all hover:shadow-xl hover:shadow-[#ff0050]/10 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-[#ff0050]/0 to-[#ff0050]/0 group-hover:from-[#ff0050]/5 group-hover:to-transparent rounded-xl transition-all duration-500"></div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-3xl font-bold text-[#ff0050]/20 group-hover:text-[#ff0050]/40 transition-colors">04</div>
                    <div className="w-10 h-10 bg-gradient-to-br from-[#ff0050]/20 to-[#00f2ea]/20 rounded-lg flex items-center justify-center group-hover:from-[#ff0050]/30 group-hover:to-[#00f2ea]/30 transition-all group-hover:scale-110 border border-[#ff0050]/30">
                      <i className="ri-line-chart-line text-xl text-[#ff0050]"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#ff0050] transition-colors">Acompanhe Resultados</h3>
                  <p className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">Monitore seu crescimento em tempo real com métricas detalhadas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative py-24 bg-[#0a0a0a] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 animate-pulse"></div>
        </div>
        
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00f2ea] to-transparent animate-pulse"></div>
          <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff0050] to-transparent animate-pulse"></div>
          <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00f2ea] to-transparent animate-pulse"></div>
          <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff0050] to-transparent animate-pulse"></div>
        </div>
        
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-[#00f2ea] rounded-full animate-ping opacity-40"></div>
          <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-[#ff0050] rounded-full animate-pulse opacity-30"></div>
          <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-[#00f2ea] rounded-full animate-ping opacity-20"></div>
          <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-[#ff0050] rounded-full animate-pulse opacity-25"></div>
          <div className="absolute top-2/3 left-1/2 w-2 h-2 bg-[#00f2ea] rounded-full animate-ping opacity-30"></div>
          <div className="absolute top-1/5 right-1/5 w-3 h-3 bg-[#ff0050] rounded-full animate-pulse opacity-20"></div>
        </div>
        
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#00f2ea]/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#ff0050]/10 rounded-full blur-[120px] animate-pulse"></div>
        
        <div className="absolute top-12 left-12 w-24 h-24 border-l-2 border-t-2 border-[#00f2ea]/30 animate-pulse"></div>
        <div className="absolute top-12 right-12 w-24 h-24 border-r-2 border-t-2 border-[#ff0050]/30 animate-pulse"></div>
        <div className="absolute bottom-12 left-12 w-24 h-24 border-l-2 border-b-2 border-[#00f2ea]/30 animate-pulse"></div>
        <div className="absolute bottom-12 right-12 w-24 h-24 border-r-2 border-b-2 border-[#ff0050]/30 animate-pulse"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00f2ea]/10 backdrop-blur-sm rounded-full mb-6 border border-[#00f2ea]/30 animate-pulse">
              <div className="w-2 h-2 bg-[#00f2ea] rounded-full animate-ping"></div>
              <span className="text-sm font-semibold bg-gradient-to-r from-[#00f2ea] to-[#ff0050] bg-clip-text text-transparent">Planos e Preços</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Escolha seu <span className="bg-gradient-to-r from-[#00f2ea] to-[#ff0050] bg-clip-text text-transparent relative">
                plano
                <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#00f2ea] to-[#ff0050] animate-pulse"></div>
              </span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Basic Plan */}
            <div className="group relative">
              <div className="relative h-full bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border transition-all hover:shadow-2xl hover:-translate-y-2 border-gray-800 hover:border-[#00f2ea]/50 hover:shadow-[#00f2ea]/20">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00f2ea]/0 to-[#00f2ea]/0 group-hover:from-[#00f2ea]/10 group-hover:to-transparent rounded-2xl transition-all duration-500"></div>
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00f2ea]/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00f2ea]/20 to-transparent"></div>
                </div>
                <div className="absolute top-4 right-4 w-1 h-1 bg-[#00f2ea] rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
                <div className="absolute bottom-4 left-4 w-1 h-1 bg-[#00f2ea] rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
                
                <div className="relative flex flex-col h-full">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#00f2ea] transition-colors">Plano Básico</h3>
                    <p className="text-sm text-gray-400">Perfeito para começar</p>
                  </div>
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-white group-hover:text-[#00f2ea] transition-colors">R$ 149</span>
                      <span className="text-gray-400">/mês</span>
                    </div>
                  </div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#00f2ea]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#00f2ea]/30 transition-all">
                        <i className="ri-check-line text-[#00f2ea] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Gerador de Vídeo com IA</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#00f2ea]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#00f2ea]/30 transition-all">
                        <i className="ri-check-line text-[#00f2ea] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Produtos em Alta para se afiliar</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#00f2ea]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#00f2ea]/30 transition-all">
                        <i className="ri-check-line text-[#00f2ea] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Espionagem de Creators que mais vendem no TikTok Shop</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#00f2ea]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#00f2ea]/30 transition-all">
                        <i className="ri-check-line text-[#00f2ea] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Análise de métricas</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#00f2ea]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#00f2ea]/30 transition-all">
                        <i className="ri-check-line text-[#00f2ea] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Suporte por email</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#00f2ea]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#00f2ea]/30 transition-all">
                        <i className="ri-check-line text-[#00f2ea] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Dashboard completo</span>
                    </li>
                  </ul>
                  <a 
                    href="https://go.perfectpay.com.br/PPU38CQ6BQI" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="relative w-full px-6 py-4 rounded-lg font-bold text-lg transition-all cursor-pointer whitespace-nowrap overflow-hidden text-center bg-white/10 text-white hover:bg-white/20 border border-white/20 hover:border-[#00f2ea]/50 block"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative z-10">Começar Agora</span>
                  </a>
                </div>
              </div>
            </div>
            
            {/* Premium Plan */}
            <div className="group relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-[#00f2ea] to-[#ff0050] text-white rounded-full text-sm font-bold whitespace-nowrap z-10 animate-pulse">Mais Popular</div>
              <div className="relative h-full bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border transition-all hover:shadow-2xl hover:-translate-y-2 border-[#ff0050]/50 hover:border-[#ff0050] shadow-lg shadow-[#ff0050]/20 hover:shadow-[#ff0050]/30">
                <div className="absolute inset-0 bg-gradient-to-br from-[#ff0050]/0 to-[#ff0050]/0 group-hover:from-[#ff0050]/10 group-hover:to-transparent rounded-2xl transition-all duration-500"></div>
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff0050] to-transparent"></div>
                  <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff0050] to-transparent"></div>
                </div>
                <div className="absolute top-4 right-4 w-1 h-1 bg-[#ff0050] rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
                <div className="absolute bottom-4 left-4 w-1 h-1 bg-[#ff0050] rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
                
                <div className="relative flex flex-col h-full">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#ff0050] transition-colors">Plano Premium</h3>
                    <p className="text-sm text-gray-400">Mais popular entre criadores</p>
                  </div>
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-white group-hover:text-[#ff0050] transition-colors">R$ 249</span>
                      <span className="text-gray-400">/Vitalício</span>
                    </div>
                  </div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#ff0050]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#ff0050]/30 transition-all">
                        <i className="ri-check-line text-[#ff0050] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Gerador de Vídeo com IA</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#ff0050]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#ff0050]/30 transition-all">
                        <i className="ri-check-line text-[#ff0050] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Produtos em Alta para se afiliar</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#ff0050]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#ff0050]/30 transition-all">
                        <i className="ri-check-line text-[#ff0050] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Espionagem de Creators que mais vendem no TikTok Shop</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#ff0050]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#ff0050]/30 transition-all">
                        <i className="ri-check-line text-[#ff0050] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Análise detalhada e insights</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#ff0050]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#ff0050]/30 transition-all">
                        <i className="ri-check-line text-[#ff0050] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Suporte prioritário 24/7</span>
                    </li>
                    <li className="flex items-start gap-3 group/item">
                      <div className="w-5 h-5 bg-[#ff0050]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#ff0050]/30 transition-all">
                        <i className="ri-check-line text-[#ff0050] text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">Relatórios personalizados</span>
                    </li>
                  </ul>
                  <a 
                    href="https://go.perfectpay.com.br/PPU38CQ6BQH" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="relative w-full px-6 py-4 rounded-lg font-bold text-lg transition-all cursor-pointer whitespace-nowrap overflow-hidden text-center bg-gradient-to-r from-[#00f2ea] to-[#ff0050] text-white hover:shadow-xl hover:shadow-[#ff0050]/50 hover:scale-105 block"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <div className="absolute inset-0 border border-[#00f2ea]/0 group-hover:border-[#00f2ea]/50 transition-all duration-300 rounded-lg"></div>
                    <span className="relative z-10">Começar Agora</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <p className="text-gray-400 mb-4">Todos os planos incluem garantia de 7 dias</p>
            <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-[#00f2ea]"></i>
                <span>Pagamento seguro</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-refresh-line text-[#00f2ea]"></i>
                <span>Cancele quando quiser</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-customer-service-line text-[#00f2ea]"></i>
                <span>Suporte dedicado</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#0f0f0f] to-[#1a1a1a] text-white py-16 px-6 border-t border-[#00f2ea]/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#00f2ea] to-[#ff0050] rounded-lg flex items-center justify-center shadow-lg shadow-[#00f2ea]/30">
                  <span className="text-white font-bold text-lg">T</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">TikScale</h3>
                  <p className="text-xs text-gray-400">Escale seu TikTokShop</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">A plataforma mais inteligente para crescer no TikTok de forma automática e segura.</p>
              <div className="flex gap-3">
                <a href="#" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-[#00f2ea] hover:to-[#ff0050] transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-[#00f2ea] hover:to-[#ff0050] transition-all cursor-pointer">
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-[#00f2ea] hover:to-[#ff0050] transition-all cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-[#00f2ea] hover:to-[#ff0050] transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Produto</h4>
              <ul className="space-y-3">
                <li><a href="#features" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Recursos</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Preços</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Demonstração</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Atualizações</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Empresa</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Sobre Nós</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Carreiras</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Contato</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Suporte</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Central de Ajuda</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Documentação</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Status do Sistema</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">FAQ</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">© 2025 TikScale. Todos os direitos reservados.</p>
              <div className="flex gap-6">
                <a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Privacidade</a>
                <a href="#" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Termos</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#00f2ea] transition-colors text-sm cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
